import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import './FogControls.css';

const FogControls = ({
  onClose,
  triggerRef,
  anchorRect,
  fogOfWar,
  onFogToggle,
  onFogOpacityChange,
  onFogIgnoreGMToggle,
  onFogRevealAll,
  onFogCoverAll,
  fogEditMode,
  onFogModeChange,
  // GAP-003: shape palette — date_of_change: 2026-05-20
  fogShape = 'rect',
  onFogShapeChange,
  onFogUndo,
  canUndoFog = false,
}) => {
  const panelRef = useRef(null);

  const closePanel = useCallback(() => {
    onClose();
    window.requestAnimationFrame?.(() => triggerRef?.current?.focus?.());
  }, [onClose, triggerRef]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      const clickedInsidePanel = panelRef.current?.contains(event.target);
      const clickedTrigger = triggerRef?.current?.contains?.(event.target);
      if (!clickedInsidePanel && !clickedTrigger) {
        closePanel();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closePanel();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closePanel, triggerRef]);

  const closeWhenMapGestureIsReady = useCallback(() => {
    // Mobile map tools must yield the canvas after configuration. Desktop keeps
    // the anchored flyout open for rapid adjustments. date_of_change: 2026-09-26
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      closePanel();
    }
  }, [closePanel]);

  const selectMode = (mode) => {
    if (typeof onFogModeChange === 'function') {
      onFogModeChange(mode);
    }
    if (mode !== 'none') {
      closeWhenMapGestureIsReady();
    }
  };

  const selectShape = (shape) => {
    onFogShapeChange?.(shape);
    if (fogEditMode !== 'none') {
      closeWhenMapGestureIsReady();
    }
  };

  const fogEnabled = !!fogOfWar?.enabled;
  const gmIgnoreFog = !!fogOfWar?.ignoreGM;
  const opacityValue = fogOfWar?.opacity ?? 0.85;
  const panelStyle = useMemo(() => {
    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1280;
    if (viewportWidth < 768) {
      return {
        position: 'fixed',
        top: 'auto',
        right: 8,
        bottom: 'max(8px, env(safe-area-inset-bottom))',
        left: 8,
        width: 'auto'
      };
    }
    const left = Math.max(12, Math.min(anchorRect?.left ?? 12, viewportWidth - 332));
    const top = Math.max((anchorRect?.bottom ?? 0) + 6, 12);
    return {
      position: 'fixed',
      top,
      left
    };
  }, [anchorRect]);

  const panel = (
    <div
      ref={panelRef}
      className="fog-controls-panel"
      role="dialog"
      aria-label="Fog tools"
      style={panelStyle}
    >
      <div className="fog-controls-panel-header">
        <div className="fog-controls-panel-title">
          <span aria-hidden="true">🌫️</span>
          <span>Fog Tools</span>
        </div>
        <button
          type="button"
          className="fog-controls-panel-close"
          onClick={closePanel}
          aria-label="Close fog tools"
        >
          ×
        </button>
      </div>

      <div className="fog-controls-panel-body">
        <p className="fog-controls-mobile-instruction">
          {fogEditMode === 'none'
            ? 'Choose Reveal or Cover to close this sheet and paint on the map.'
            : 'Choose a shape to close this sheet and continue painting on the map.'}
        </p>

        <div className="fog-controls-row">
          <span className="fog-controls-label">Show Fog</span>
          <button
            type="button"
            onClick={onFogToggle}
            className={`fog-controls-toggle ${fogEnabled ? 'is-active' : ''}`}
            aria-pressed={fogEnabled}
          >
            {fogEnabled ? 'On' : 'Off'}
          </button>
        </div>

        <div className="fog-controls-row">
          <span className="fog-controls-label">GM Sees Through</span>
          <button
            type="button"
            onClick={onFogIgnoreGMToggle}
            className={`fog-controls-toggle ${gmIgnoreFog ? 'is-active is-gm' : ''}`}
            aria-pressed={gmIgnoreFog}
          >
            {gmIgnoreFog ? 'On' : 'Off'}
          </button>
        </div>

        <div className="fog-controls-row fog-controls-row-slider">
          <label htmlFor="fogOpacityCompact" className="fog-controls-label">Opacity</label>
          <div className="fog-controls-slider-wrap">
            <input
              id="fogOpacityCompact"
              type="range"
              min="0.1"
              max="1.0"
              step="0.05"
              value={opacityValue}
              onChange={(event) => onFogOpacityChange?.(parseFloat(event.target.value))}
              className="fog-controls-slider"
            />
            <span className="fog-controls-slider-value">{Math.round(opacityValue * 100)}%</span>
          </div>
        </div>

        <div className="fog-controls-section">
          <span className="fog-controls-section-label">Drag Tool</span>
          <div className="fog-controls-mode-group">
            <button
              type="button"
              className={`fog-controls-mode ${fogEditMode === 'none' ? 'is-active is-neutral' : ''}`}
              onClick={() => selectMode('none')}
              aria-pressed={fogEditMode === 'none'}
            >
              Off
            </button>
            <button
              type="button"
              className={`fog-controls-mode ${fogEditMode === 'reveal' ? 'is-active is-reveal' : ''}`}
              onClick={() => selectMode('reveal')}
              aria-pressed={fogEditMode === 'reveal'}
            >
              Reveal
            </button>
            <button
              type="button"
              className={`fog-controls-mode ${fogEditMode === 'cover' ? 'is-active is-cover' : ''}`}
              onClick={() => selectMode('cover')}
              aria-pressed={fogEditMode === 'cover'}
            >
              Cover
            </button>
          </div>
        </div>

        {/* GAP-003: Shape palette — rect / polygon / lasso — date_of_change: 2026-05-20 */}
        <div className="fog-controls-section">
          <span className="fog-controls-section-label">Shape</span>
          <div className="fog-controls-mode-group">
            <button
              type="button"
              className={`fog-controls-mode ${fogShape === 'rect' ? 'is-active is-neutral' : ''}`}
              onClick={() => selectShape('rect')}
              aria-pressed={fogShape === 'rect'}
              title="Rectangle drag tool"
              data-testid="fog-shape-rect"
            >
              ▭ Rect
            </button>
            <button
              type="button"
              className={`fog-controls-mode ${fogShape === 'polygon' ? 'is-active is-reveal' : ''}`}
              onClick={() => selectShape('polygon')}
              aria-pressed={fogShape === 'polygon'}
              title="Polygon: tap or click to place vertices, double-tap or double-click to close"
              data-testid="fog-shape-polygon"
            >
              ⬡ Poly
            </button>
            <button
              type="button"
              className={`fog-controls-mode ${fogShape === 'lasso' ? 'is-active is-cover' : ''}`}
              onClick={() => selectShape('lasso')}
              aria-pressed={fogShape === 'lasso'}
              title="Lasso: click-drag to draw freeform region"
              data-testid="fog-shape-lasso"
            >
              ⌖ Lasso
            </button>
          </div>
        </div>

        <div className="fog-controls-section">
          <span className="fog-controls-section-label">Quick Fill</span>
          <div className="fog-controls-action-row">
            <button
              type="button"
              className="fog-controls-action fog-controls-action-reveal"
              onClick={onFogRevealAll}
            >
              Reveal All
            </button>
            <button
              type="button"
              className="fog-controls-action fog-controls-action-cover"
              onClick={onFogCoverAll}
            >
              Cover All
            </button>
          </div>
        </div>

        {/* GAP-003: Undo last fog operation — date_of_change: 2026-05-20 */}
        {typeof onFogUndo === 'function' && (
          <div className="fog-controls-section">
            <button
              type="button"
              className={`fog-controls-action ${canUndoFog ? '' : 'is-disabled'}`}
              onClick={onFogUndo}
              disabled={!canUndoFog}
              title="Undo last fog operation"
              data-testid="fog-undo"
              style={{ width: '100%' }}
            >
              ↩ Undo Last Op
            </button>
          </div>
        )}

        <p className="fog-controls-hint">
          {fogShape === 'rect' && <><strong>Rect</strong>: drag to paint a rectangle.</>}
          {fogShape === 'polygon' && <><strong>Polygon</strong>: tap or click vertices, double-tap or double-click to close.</>}
          {fogShape === 'lasso' && <><strong>Lasso</strong>: drag to draw a freeform region.</>}
        </p>
      </div>
    </div>
  );

  if (typeof document === 'undefined' || !document.body) {
    return panel;
  }

  return createPortal(panel, document.body);
};

FogControls.propTypes = {
  onClose: PropTypes.func.isRequired,
  triggerRef: PropTypes.shape({ current: PropTypes.any }),
  anchorRect: PropTypes.shape({
    top: PropTypes.number,
    left: PropTypes.number,
    bottom: PropTypes.number,
    right: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  fogOfWar: PropTypes.shape({
    enabled: PropTypes.bool,
    opacity: PropTypes.number,
    ignoreGM: PropTypes.bool,
  }),
  onFogToggle: PropTypes.func,
  onFogOpacityChange: PropTypes.func,
  onFogIgnoreGMToggle: PropTypes.func,
  onFogRevealAll: PropTypes.func,
  onFogCoverAll: PropTypes.func,
  fogEditMode: PropTypes.oneOf(['none', 'reveal', 'cover']),
  onFogModeChange: PropTypes.func,
  // GAP-003: shape palette — date_of_change: 2026-05-20
  fogShape: PropTypes.oneOf(['rect', 'polygon', 'lasso']),
  onFogShapeChange: PropTypes.func,
  onFogUndo: PropTypes.func,
  canUndoFog: PropTypes.bool,
};

export default FogControls;
